/**
 * NOS-217: sample an image's dominant luminance client-side so the footer can
 * decide whether the logo needs a visibility treatment (FooterLogo).
 * Extended for the knockout treatment: the sample also reports whether the
 * image has meaningful transparency (a silhouette filter only makes sense on
 * transparent-background logos).
 *
 * Mechanism: load the image with crossOrigin="anonymous", draw it onto a tiny
 * canvas, and average the WCAG relative luminance of its opaque pixels.
 * Results are cached per URL for the session. Sampling is strictly
 * best-effort: cross-origin images without CORS headers (e.g. external SVG
 * logos, which are excluded from rehosting by NOS-179 policy), canvas
 * unavailability, or load errors all yield "unknown"/null — the caller then
 * applies its safe default (chip on dark backgrounds).
 */
import { useEffect, useState } from "react"
import {
  averageLuminanceOfPixels,
  classifyLuminance,
  hasMeaningfulAlpha,
  type ImageLuminance,
} from "@/lib/colorContrast"

const SAMPLE_SIZE = 24

export interface ImageSample {
  luminance: ImageLuminance
  /** True/false when sampled; null when sampling was impossible. */
  hasAlpha: boolean | null
}

const UNKNOWN_SAMPLE: ImageSample = { luminance: "unknown", hasAlpha: null }

/** Session cache: sampling a logo once per URL is plenty. */
const cache = new Map<string, ImageSample>()

function sampleImage(img: HTMLImageElement): ImageSample {
  try {
    const canvas = document.createElement("canvas")
    canvas.width = SAMPLE_SIZE
    canvas.height = SAMPLE_SIZE
    const ctx = canvas.getContext("2d")
    if (!ctx) return UNKNOWN_SAMPLE
    ctx.drawImage(img, 0, 0, SAMPLE_SIZE, SAMPLE_SIZE)
    // Throws on tainted canvases (image without CORS approval) → unknown.
    const data = ctx.getImageData(0, 0, SAMPLE_SIZE, SAMPLE_SIZE).data
    return {
      luminance: classifyLuminance(averageLuminanceOfPixels(data)),
      hasAlpha: hasMeaningfulAlpha(data),
    }
  } catch {
    return UNKNOWN_SAMPLE
  }
}

/**
 * Sample an image URL: luminance classification + transparency presence.
 * Returns the unknown sample while sampling is in flight (or forever, when
 * sampling is impossible). Pass null to skip sampling entirely.
 */
export function useImageSample(src: string | null | undefined): ImageSample {
  const [value, setValue] = useState<ImageSample>(() =>
    src ? cache.get(src) ?? UNKNOWN_SAMPLE : UNKNOWN_SAMPLE
  )

  useEffect(() => {
    if (!src) {
      setValue(UNKNOWN_SAMPLE)
      return
    }
    const cached = cache.get(src)
    if (cached) {
      setValue(cached)
      return
    }
    // Test environments (happy-dom) have no real image pipeline — stay
    // deterministic (unknown → safe chip default) instead of racing async
    // load/error callbacks outside act().
    if (import.meta.env?.MODE === "test") return

    let cancelled = false
    const img = new Image()
    img.crossOrigin = "anonymous"
    img.onload = () => {
      const result = sampleImage(img)
      cache.set(src, result)
      if (!cancelled) setValue(result)
    }
    img.onerror = () => {
      cache.set(src, UNKNOWN_SAMPLE)
      if (!cancelled) setValue(UNKNOWN_SAMPLE)
    }
    img.src = src
    return () => {
      cancelled = true
    }
  }, [src])

  return value
}

/** Back-compat wrapper: luminance classification only. */
export function useImageLuminance(src: string | null | undefined): ImageLuminance {
  return useImageSample(src).luminance
}
